import {
  STOCKER_2D_DEFAULT_COLUMNS,
  STOCKER_2D_DEFAULT_ROWS,
} from './stocker2dPalette';
import type { Stocker2DFoupKind } from './types';

/**
 * Design locked to the STK-01 reference illustration.
 * Tall cabinet · 14×2 trays · blue accent · wide dark base.
 * The bay is centred horizontally; `padX` is the minimum side margin.
 */
export const STOCKER_2D_DESIGN = {
  width: 124,
  height: 252,
  stroke: 1.6,
  slotW: 36,
  slotH: 11.5,
  columnGap: 6,
  rowGap: 2.2,
  bayInset: 4,
  accentH: 4,
  baseH: 8,
  baseOverhang: 4,
  footH: 4,
  footW: 14,
  padX: 8,
  padTop: 28,
  padBottom: 16,
} as const;

export type StockerBayLayout = {
  startX: number;
  startY: number;
  slotW: number;
  slotH: number;
  columnGap: number;
  rowGap: number;
  rows: number;
  columns: number;
  bayW: number;
  bayH: number;
  insetX: number;
  insetY: number;
  insetW: number;
  insetH: number;
};

export function getStocker2DLayout(width: number, height: number) {
  const aspect = STOCKER_2D_DESIGN.width / STOCKER_2D_DESIGN.height;
  let cw = width * 0.96;
  let ch = cw / aspect;
  if (ch > height * 0.98) {
    ch = height * 0.98;
    cw = ch * aspect;
  }
  return {
    s: cw / STOCKER_2D_DESIGN.width,
    ox: (width - cw) / 2,
    oy: (height - ch) / 2,
    cw,
    ch,
  };
}

export function getStockerBayLayout(
  rows = STOCKER_2D_DEFAULT_ROWS,
  columns = STOCKER_2D_DEFAULT_COLUMNS
): StockerBayLayout {
  const D = STOCKER_2D_DESIGN;
  const r = Math.max(1, rows);
  const c = Math.max(1, columns);
  const maxBayW = D.width - D.padX * 2 - D.bayInset * 2;
  const maxBayH =
    D.height -
    D.footH -
    D.baseH -
    D.accentH -
    D.padTop -
    D.padBottom -
    D.bayInset * 2;
  const columnGap = Math.min(D.columnGap, maxBayW * 0.025);
  const rowGap = Math.min(D.rowGap, maxBayH * 0.015);
  const slotW = Math.min(D.slotW, (maxBayW - (c - 1) * columnGap) / c);
  const slotH = Math.min(D.slotH * 2.2, (maxBayH - (r - 1) * rowGap) / r);
  const bayW = c * slotW + (c - 1) * columnGap;
  const bayH = r * slotH + (r - 1) * rowGap;
  const insetW = bayW + D.bayInset * 2;
  const insetH = bayH + D.bayInset * 2;
  const insetX = Math.max(D.padX, (D.width - insetW) / 2);
  const insetY = D.padTop;
  const startX = insetX + D.bayInset;
  const startY = insetY + D.bayInset;
  return {
    startX,
    startY,
    slotW,
    slotH,
    columnGap,
    rowGap,
    rows: r,
    columns: c,
    bayW,
    bayH,
    insetX,
    insetY,
    insetW,
    insetH,
  };
}

export function getStockerSlotPosition(
  width: number,
  height: number,
  row: number,
  column: number,
  rows = STOCKER_2D_DEFAULT_ROWS,
  columns = STOCKER_2D_DEFAULT_COLUMNS
): { x: number; y: number } {
  const { s, ox, oy } = getStocker2DLayout(width, height);
  const bay = getStockerBayLayout(rows, columns);
  const rr = Math.max(0, Math.min(bay.rows - 1, Math.floor(row)));
  const cc = Math.max(0, Math.min(bay.columns - 1, Math.floor(column)));
  const cx = bay.startX + cc * (bay.slotW + bay.columnGap) + bay.slotW / 2;
  const cy = bay.startY + rr * (bay.slotH + bay.rowGap) + bay.slotH / 2;
  return { x: ox + cx * s, y: oy + cy * s };
}

export function buildOccupiedSlotSet(
  rows: number,
  columns: number,
  occupiedCount: number
): Set<string> {
  const total = rows * columns;
  const n = Math.max(0, Math.min(total, Math.floor(occupiedCount)));
  const set = new Set<string>();
  for (let i = 0; i < n; i++) {
    set.add(`${Math.floor(i / columns)}:${i % columns}`);
  }
  return set;
}

/**
 * Rack side a column belongs to: the left half stores raw FOUPs, the right half
 * processed ones, mirroring the AMHS raw/processed stocker columns. A
 * single-column rack has no side to spare, so every column reads as raw.
 */
export function stockerColumnKind(column: number, columns: number): Stocker2DFoupKind {
  if (columns < 2) return 'raw';
  return column < Math.ceil(columns / 2) ? 'raw' : 'processed';
}

/**
 * Place `rawCount` raw and `processedCount` processed FOUPs into the rack.
 * Each kind fills its own columns first and only spills onto the other side
 * once its own columns are full.
 */
export function buildStockerSlotKinds(
  rows: number,
  columns: number,
  rawCount: number,
  processedCount: number
): Map<string, Stocker2DFoupKind> {
  const r = Math.max(1, Math.floor(rows));
  const c = Math.max(1, Math.floor(columns));
  const rawKeys: string[] = [];
  const processedKeys: string[] = [];
  for (let row = 0; row < r; row++) {
    for (let column = 0; column < c; column++) {
      const key = `${row}:${column}`;
      if (stockerColumnKind(column, c) === 'raw') rawKeys.push(key);
      else processedKeys.push(key);
    }
  }
  // Single-column racks share one column, so raw stacks from the top and
  // processed from the bottom to keep the two readable.
  const hasSplitColumns = processedKeys.length > 0;
  const rawOrder = hasSplitColumns ? [...rawKeys, ...processedKeys] : rawKeys;
  const processedOrder = hasSplitColumns
    ? [...processedKeys, ...rawKeys]
    : [...rawKeys].reverse();

  const kinds = new Map<string, Stocker2DFoupKind>();
  const place = (order: readonly string[], count: number, kind: Stocker2DFoupKind) => {
    let remaining = Math.max(0, Math.floor(count));
    for (const key of order) {
      if (remaining === 0) break;
      if (kinds.has(key)) continue;
      kinds.set(key, kind);
      remaining -= 1;
    }
  };
  place(rawOrder, rawCount, 'raw');
  place(processedOrder, processedCount, 'processed');
  return kinds;
}

/**
 * Reference occupancy (~15/28) matching the product image:
 * left column mostly filled, right column sparsely filled.
 */
export function buildReferenceOccupiedSet(rows: number, columns: number): Set<string> {
  const set = new Set<string>();
  if (columns < 1 || rows < 1) return set;
  // Left column: 10 occupied (empty 3, 6, 10, 12)
  for (let r = 0; r < rows; r++) {
    if (r !== 3 && r !== 6 && r !== 10 && r !== 12) set.add(`${r}:0`);
  }
  // Right column: 5 occupied
  for (const r of [1, 4, 8, 11, 13]) {
    if (r < rows && columns > 1) set.add(`${r}:1`);
  }
  return set;
}
